import { useState } from "react";
import { Building2, Plus, ChevronRight, Search } from "lucide-react";
import { type Client } from "../../shared/domain";
import { api, errorMessage } from "../api";
import { Field, Modal, Notice, Spinner } from "./Common";
const emptyClient = (): Client => ({
  id: crypto.randomUUID(),
  businessName: "",
  billingAddress: {
    line1: "",
    line2: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
  },
  contactName: "",
  email: "",
  phone: "",
  vatRegistrationNumber: "",
  defaultPaymentTermDays: 1,
  defaultServiceDescription: "Logistics services",
  version: 1,
});
export function Customers({
  clients,
  query,
  onQuery,
  onEdit,
  onNew,
}: {
  clients: Client[];
  query: string;
  onQuery: (query: string) => void;
  onEdit: (c: Client) => void;
  onNew: () => void;
}) {
  const shown = clients.filter((c) =>
    c.businessName.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <>
      <div className="page-title">
        <div>
          <h1>Customers</h1>
          <p className="muted">Saved details, ready for the next invoice.</p>
        </div>
        <button className="primary desktop-action" onClick={onNew}>
          <Plus size={18} />
          Add customer
        </button>
      </div>
      <button className="primary mobile-action" onClick={onNew}>
        <Plus size={18} />
        Add customer
      </button>
      <label className="search">
        <Search size={18} />
        <input
          aria-label="Search customers"
          placeholder="Search customers"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
        />
      </label>
      <div className="ledger">
        {shown.length ? (
          shown.map((c) => (
            <button
              className="customer-row"
              key={c.id}
              onClick={() => onEdit(c)}
            >
              <span className="customer-icon">
                <Building2 size={20} />
              </span>
              <span>
                <strong>{c.businessName}</strong>
                <small>
                  {[c.billingAddress.city, c.billingAddress.postcode]
                    .filter(Boolean)
                    .join(" · ")}
                </small>
              </span>
              <ChevronRight size={18} />
            </button>
          ))
        ) : (
          <div className="empty-state">
            <Building2 size={36} />
            <h2>
              {query ? "No customers found" : "Your customers, in one place"}
            </h2>
            <p>
              {query
                ? "Try a different name."
                : "Add billing details once and reuse them on every invoice."}
            </p>
            {!query && (
              <button className="primary" onClick={onNew}>
                <Plus size={18} />
                Add your first customer
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
export function CustomerForm({
  initial,
  onClose,
  onSave,
}: {
  initial: Client | null;
  onClose: () => void;
  onSave: (c: Client) => void;
}) {
  const [c, setC] = useState(initial || emptyClient),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  return (
    <Modal
      title={initial ? "Customer details" : "Add a customer"}
      onClose={onClose}
    >
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError("");
          try {
            onSave(await api<Client>(`/clients/${c.id}`, "PUT", c));
          } catch (err) {
            setError(errorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        {error && <Notice type="error">{error}</Notice>}
        <Field label="Business name" required>
          <input
            value={c.businessName}
            maxLength={150}
            autoComplete="organization"
            required
            onChange={(e) => setC({ ...c, businessName: e.target.value })}
          />
        </Field>
        <Field label="Address line 1" required>
          <input
            value={c.billingAddress.line1}
            maxLength={150}
            required
            autoComplete="address-line1"
            onChange={(e) =>
              setC({
                ...c,
                billingAddress: { ...c.billingAddress, line1: e.target.value },
              })
            }
          />
        </Field>
        <Field label="Address line 2">
          <input
            value={c.billingAddress.line2}
            maxLength={150}
            autoComplete="address-line2"
            onChange={(e) =>
              setC({
                ...c,
                billingAddress: { ...c.billingAddress, line2: e.target.value },
              })
            }
          />
        </Field>
        <div className="two-columns">
          <Field label="Town / city" required>
            <input
              value={c.billingAddress.city}
              maxLength={100}
              required
              autoComplete="address-level2"
              onChange={(e) =>
                setC({
                  ...c,
                  billingAddress: { ...c.billingAddress, city: e.target.value },
                })
              }
            />
          </Field>
          <Field label="Postcode" required>
            <input
              value={c.billingAddress.postcode}
              maxLength={20}
              required
              autoComplete="postal-code"
              onChange={(e) =>
                setC({
                  ...c,
                  billingAddress: {
                    ...c.billingAddress,
                    postcode: e.target.value,
                  },
                })
              }
            />
          </Field>
        </div>
        <Field label="Country">
          <input
            value={c.billingAddress.country}
            maxLength={80}
            onChange={(e) =>
              setC({
                ...c,
                billingAddress: {
                  ...c.billingAddress,
                  country: e.target.value,
                },
              })
            }
          />
        </Field>
        <div className="two-columns">
          <Field label="Contact name">
            <input
              value={c.contactName}
              maxLength={120}
              onChange={(e) => setC({ ...c, contactName: e.target.value })}
            />
          </Field>
          <Field label="Email">
            <input
              value={c.email}
              type="email"
              autoComplete="email"
              onChange={(e) => setC({ ...c, email: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Payment terms (days)">
          <input
            type="number"
            min={0}
            max={365}
            value={c.defaultPaymentTermDays}
            onChange={(e) =>
              setC({ ...c, defaultPaymentTermDays: Number(e.target.value) })
            }
          />
        </Field>
        <div className="dialog-actions">
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="primary" disabled={busy}>
            {busy && <Spinner />}Save customer
          </button>
        </div>
      </form>
    </Modal>
  );
}
